import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { RootState } from "../store"; // Assuming you have RootState defined for your Vuex 4 store
import { GridRefUtils } from "../../../../utils/helpers/GridRefPreference";
import { MeasurementPrefUtility } from "../../../../utils/helpers/MeasurementPref";
import { isClickOnWaypointHelper } from "../../../../utils/helpers/IsClickOnWaypointHelper";
import mapboxgl from "mapbox-gl";
import {HiikerMapStyle, SavedCenter} from "../../../../utils/models/MapStyles";
import { getElevation } from "../../../../utils/crosshair_altitude_service/pixel_methods";
import { IndexedDBCache } from "../../../../utils/crosshair_altitude_service/indexed_db_cache";
import { throttle } from 'lodash-es';
import {RevenueCatWrapper} from "../../../../utils/RevenueCatWrapper";

// MapState class remains the same - defines the shape of the state
export class MapState {
  center: mapboxgl.LngLatLike = [0, 0];
  miniCoordsBar: string = '';
  mapInitialised: boolean = false;
  zoom: number = 0;
  loading: boolean = false;
  pitch: number = 0;
  bearing: number = 0;
  marker: mapboxgl.Marker | null = null;
}

// State factory function - recommended pattern in Vuex 4
// Returns a fresh state object each time, important for SSR and module reuse/reset
const state = (): MapState => {
  return new MapState(); // Or return a plain object literal matching the MapState structure
};

// Actions remain the same - signature and context structure are compatible
const actions: ActionTree<MapState, RootState> = {
  mapInitialised(store) {
    store.commit('setMapInitialised', true);
    const gridRef = GridRefUtils.preference();
    let measurementPreference = MeasurementPrefUtility.preference();
    // Assuming window.map is still managed globally or passed appropriately
    // Be cautious with global references like window.map in larger applications
    if (window.map) {
      window.map.on('click', (e) => {
        store.dispatch('mapClicked', e);
      });

      const cache = new IndexedDBCache();
      const handleMouseMove = async (e: mapboxgl.MapMouseEvent & Object) => {
        const coords = gridRef.convert(e.lngLat);
        const elevation = await getElevation(e.lngLat, e.target.getZoom(), cache);
        store.commit('setMiniCoordsBar', `${coords.toString()} ${elevation}`);
      };
      const throttledMouseMove = throttle(handleMouseMove, 20); // Adjust the throttle interval as needed
      window.map.on('mousemove', throttledMouseMove);
    } else {
      console.error("Mapbox map instance (window.map) not found during mapInitialised action.");
    }
  },
  mapClicked(store, e: mapboxgl.MapMouseEvent & Object) {
    console.log('map clicked', e);
    // Accessing rootState remains the same
    if (store.rootState.waypoints.waypointAddingModeEnabled) {
      return;
    }
    // Assuming window.map is available
    if (window.map && isClickOnWaypointHelper(window.map.queryRenderedFeatures(e.point))) {
      return;
    }
  },
};

// Mutations remain the same - signature and state modification are compatible
const mutations: MutationTree<MapState> = {
  setCenter(state, center: mapboxgl.LngLatLike) {
    state.center = center;
  },
  setMiniCoordsBar(state, miniCoordsBar: string) {
    state.miniCoordsBar = miniCoordsBar;
  },
  setMapInitialised(state, mapInitialised: boolean) {
    state.mapInitialised = mapInitialised;
  },
  setZoom(state, zoom: number) {
    state.zoom = zoom;
  },
  setLoading(state, loading: boolean) {
    state.loading = loading;
  },
  setPitch(state, pitch: number) {
    state.pitch = pitch;
  },
  setBearing(state, bearing: number) {
    state.bearing = bearing;
  }
};

// Getters remain the same - signature and state access are compatible
const getters: GetterTree<MapState, RootState> = {
  async initialMapSettings(state): Promise<Partial<mapboxgl.MapOptions>> { // Use Partial<MapboxOptions> for better type safety if not all options are set
    const savedCenter = SavedCenter.getCurrentCenter();
    const isPro = await RevenueCatWrapper.isProOrProPlus()
    const style = HiikerMapStyle.getCurrentStyle(isPro).styleUrl
    return {
      container: 'map', // container ID
      center: savedCenter.lngLat(), // starting position [lng, lat]
      zoom: savedCenter.zoom, // starting zoom
      pitch: state.pitch,
      bearing: state.bearing,
      maxPitch: 85,
      style: style, // style URL
      // style: // You might need to define a style here too
    };
  },
  getEl(): HTMLDivElement { // Explicit return type
    const el = document.createElement('div');
    el.style.backgroundImage = `url(https://storage.googleapis.com/waymarked-trails-dd329.appspot.com/Public/easy-s.png)`;
    el.style.position = 'absolute';
    el.style.width = `${37}px`;
    el.style.height = `${50}px`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';
    // el.style.offset = '0px'; // 'offset' is not a standard CSS property, maybe 'transform' or 'top/left'? Removed for now.
    el.style.marginTop = '-22px'; // This might conflict with absolute positioning depending on context
    el.style.backgroundPosition = 'center center';
    // This transform likely needs to be dynamic, setting it statically here might not be useful.
    // el.style.transform = 'translate(531px, 360px) translate(-50%, -50%) translate(0px, 0px);';
    return el;
  }
};

// Define the module as a plain object conforming to the Module interface
const mapModule: Module<MapState, RootState> = {
  namespaced: true,
  state, // Use the state factory function
  actions,
  mutations,
  getters
};

export default mapModule;

// Define window interface if using TypeScript and accessing window.map globally
// You might want to place this in a global declaration file (e.g., global.d.ts)
declare global {
  interface Window {
    map: mapboxgl.Map;
  }
}
